import i18n from '@/utils/i18n';
import InfoIcon from '@assets/icons/info.svg';
import React, { useMemo, useState } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { CreditPolicy } from '@/components/credit-request/credit-policy';
import { CreditRequestBatchLineItem } from '@/contexts/credit-request-context-types';
import {
  CreditRequestInvoiceItems_SearchVariantPacksFieldsFragment,
  ItemsContext_VariantsPackFieldsFragment,
} from '@/types';
import { CreditRequestVariantPackListItem } from '@/components/credit-request/credit-request-variant-pack-list-item';
import { ItemListOption } from '@/contexts/item-context-types';
import {
  KeyboardAwareRecyclerList,
  ListSection,
} from '@/components/recycler-list';
import { ListEmptyState } from '@/components/list-empty-state';
import { SCREEN_PADDING, SCREEN_PADDING_SMALL } from '@/styles';
import { SearchInput } from '@/components/search-input';
import { TouchableOpacity, View } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useAdaptVariantPackToItemListOption } from '@/contexts/item-context-utils';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useCreditRequestInvoiceStore } from '@/stores/credit-request-invoice-store';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useMultiSearchFilters } from '@/hooks/use-multi-search-filters';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export interface CreditRequestInvoiceItemsContentProps {
  items?: CreditRequestInvoiceItems_SearchVariantPacksFieldsFragment[];
  onNext: () => void;
  onSelectItem: (
    variantPack: ItemListOption,
    searchResultItem: CreditRequestInvoiceItems_SearchVariantPacksFieldsFragment,
    existingBatchLineItem: CreditRequestBatchLineItem | undefined,
  ) => void;
}

const HIT_SLOP = {
  bottom: 8,
  left: 8,
  right: 8,
  top: 8,
};

const getSearchableFields = (
  item: CreditRequestInvoiceItems_SearchVariantPacksFieldsFragment,
) => {
  return [
    item.variant_pack?.item.display_name || '',
    item.variant_pack?.external_item_id || '',
  ];
};

export const CreditRequestInvoiceItemsContent: React.FC<
  CreditRequestInvoiceItemsContentProps
> = ({ items, onNext, onSelectItem }) => {
  const [showCreditPolicy, setShowCreditPolicy] = useState(false);
  const { bottom } = useSafeAreaInsets();
  const adaptVariantPackToItemListOption =
    useAdaptVariantPackToItemListOption();
  const { credit_policy: creditPolicy } =
    useDynamicConfigValue('credit_request_config') ?? {};
  const { creditRequestSettings } = useAuthenticatedUserContext();
  const [styles, theme] = useStyles();

  const { filteredData, onDisplayTextChange, searchText, searching } =
    useMultiSearchFilters<CreditRequestInvoiceItems_SearchVariantPacksFieldsFragment>(
      {
        data: items ?? [],
        getSearchableFields,
      },
    );

  const batchItems = useCreditRequestInvoiceStore((s) => s.batchItems);

  const batchItemsVariantPackUUIDs = useMemo(
    () => batchItems.map((lineItem) => lineItem.item.value),
    [batchItems],
  );

  const sections = useMemo(() => {
    if (!filteredData || filteredData.length === 0) {
      return [];
    }

    const { eligible, ineligible, requestingCredit } = filteredData.reduce<{
      eligible: ItemsContext_VariantsPackFieldsFragment[];
      ineligible: ItemsContext_VariantsPackFieldsFragment[];
      requestingCredit: ItemsContext_VariantsPackFieldsFragment[];
    }>(
      (acc, { eligible_for_credit, variant_pack }) => {
        if (!variant_pack) {
          return acc;
        }

        if (batchItemsVariantPackUUIDs.includes(variant_pack.uuid)) {
          acc.requestingCredit.push(variant_pack);
        } else if (eligible_for_credit) {
          acc.eligible.push(variant_pack);
        } else {
          acc.ineligible.push(variant_pack);
        }

        return acc;
      },
      { eligible: [], ineligible: [], requestingCredit: [] },
    );

    const listSections: ListSection<
      ItemsContext_VariantsPackFieldsFragment,
      { isEligible: boolean }
    >[] = [];

    if (eligible.length > 0) {
      listSections.push({
        data: eligible,
        key: i18n.t('credit_request.eligible'),
        metadata: { isEligible: true },
      });
    }

    if (requestingCredit.length > 0) {
      listSections.push({
        data: requestingCredit,
        key: i18n.t('credit_request.requesting'),
        metadata: { isEligible: true },
      });
    }

    if (ineligible.length > 0) {
      listSections.push({
        data: ineligible,
        key: i18n.t('credit_request.not_eligible'),
        metadata: { isEligible: false },
      });
    }

    return listSections;
  }, [batchItemsVariantPackUUIDs, filteredData]);

  const variantPackResultMap = useMemo(() => {
    return (filteredData ?? []).reduce<
      Record<string, CreditRequestInvoiceItems_SearchVariantPacksFieldsFragment>
    >((acc, result) => {
      const uuid = result.variant_pack?.uuid;
      if (uuid) {
        acc[uuid] = result;
      }
      return acc;
    }, {});
  }, [filteredData]);

  const handleSelectItem = (
    variantPack: ItemListOption,
    searchResultItem: CreditRequestInvoiceItems_SearchVariantPacksFieldsFragment,
  ) => {
    const existingBatchLineItem = batchItems.find(
      (lineItem) => lineItem.item.value === variantPack.value,
    );
    onSelectItem(variantPack, searchResultItem, existingBatchLineItem);
  };

  const showNoEligibleItemsState =
    items !== undefined &&
    items.length > 0 &&
    !items.some((item) => item.eligible_for_credit) &&
    !creditRequestSettings?.request_ineligible_items_enabled;

  if (showNoEligibleItemsState) {
    return (
      <View style={styles.noEligibleItemsContainer}>
        <View style={styles.noEligibleItemsTitle}>
          <Typography variant="heading-md">
            {i18n.t('credit_request.no_eligible_items.title')}
          </Typography>
        </View>
        <View style={styles.noEligibleItemsMessage}>
          <Typography
            color={theme.sk.color.text.tertiary}
            variant="paragraph-base"
          >
            {i18n.t('credit_request.no_eligible_items.message')}
          </Typography>
        </View>
      </View>
    );
  }

  return (
    <>
      <View style={styles.searchWrapper}>
        <SearchInput
          autoCorrect={false}
          isLoading={searching}
          onChangeText={onDisplayTextChange}
          placeholder={i18n.t('invoice_search_placeholder')}
          value={searchText}
        />
      </View>
      <View style={styles.flex}>
        <KeyboardAwareRecyclerList
          alwaysBounceVertical={false}
          contentContainerStyle={{
            paddingBottom: Math.max(bottom, SCREEN_PADDING),
          }}
          estimatedItemSize={80}
          keyboardDismissMode="on-drag"
          keyboardShouldPersistTaps="handled"
          ListEmptyComponent={
            items === undefined ? (
              <View style={styles.loadingWrapper}>
                <ActivityIndicator />
              </View>
            ) : (
              <ListEmptyState
                text={i18n.t('no_results_for', {
                  searchTerm: searchText,
                })}
              />
            )
          }
          listItemSeparatorProps={{
            style: styles.itemSeparator,
          }}
          renderItem={({ item }) => {
            const option = adaptVariantPackToItemListOption(item);
            const variantPackResult = variantPackResultMap[item.uuid];
            const isEligible = variantPackResult.eligible_for_credit;

            return isEligible ||
              creditRequestSettings?.request_ineligible_items_enabled ? (
              <TouchableOpacity
                accessibilityRole="button"
                onPress={() => {
                  handleSelectItem(option, variantPackResult);
                }}
                testID="credit-request-eligible-item"
              >
                <CreditRequestVariantPackListItem
                  isSimplified
                  option={option}
                />
              </TouchableOpacity>
            ) : (
              <View
                accessibilityRole="text"
                accessibilityState={{ disabled: true }}
                accessible
                style={styles.ineligibleItem}
                testID="credit-request-ineligible-item"
              >
                <CreditRequestVariantPackListItem
                  isSimplified
                  option={option}
                />
              </View>
            );
          }}
          renderSectionHeader={(item, _target, index) => (
            <View
              style={[
                styles.sectionHeader,
                index > 0 ? styles.additionalPadding : null,
              ]}
            >
              <Typography variant="paragraph-md">{item.title}</Typography>
              {!item.metadata?.isEligible &&
              creditPolicy &&
              creditPolicy.length > 0 ? (
                <TouchableOpacity
                  accessibilityRole="button"
                  hitSlop={HIT_SLOP}
                  onPress={() => setShowCreditPolicy(true)}
                  testID="ineligible-items-info-icon"
                >
                  <InfoIcon
                    height={16}
                    stroke={theme.sk.color.icon.tertiary}
                    width={16}
                  />
                </TouchableOpacity>
              ) : null}
            </View>
          )}
          sections={sections}
          stickyHeaderIndices={[]}
          testID="credit-request-invoice-items-list"
        />
      </View>
      {batchItemsVariantPackUUIDs.length > 0 ? (
        <View
          style={[
            styles.bottomActionWrapper,
            { paddingBottom: Math.max(bottom, SCREEN_PADDING) },
          ]}
        >
          <Button onPress={onNext} trackingData={null}>
            {batchItemsVariantPackUUIDs.length === 1
              ? i18n.t('continue_with_count_singular')
              : i18n.t('continue_with_count_plural', {
                  count: batchItemsVariantPackUUIDs.length,
                })}
          </Button>
        </View>
      ) : null}
      {showCreditPolicy && creditPolicy ? (
        <BaseActionSheet isVisible onClose={() => setShowCreditPolicy(false)}>
          <View style={styles.creditPolicyWrapper}>
            <View style={styles.creditPolicyHeader}>
              <Typography variant="heading-lg">
                {i18n.t('credit_policy')}
              </Typography>
            </View>
            <CreditPolicy creditPolicy={creditPolicy} />
          </View>
          <Button
            onPress={() => {
              setShowCreditPolicy(false);
            }}
            trackingData={{
              name: 'credit_request_policy_button_ok',
            }}
          >
            {i18n.t('got_it')}
          </Button>
        </BaseActionSheet>
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  additionalPadding: {
    paddingTop: 24,
  },
  bottomActionWrapper: {
    paddingHorizontal: SCREEN_PADDING,
    paddingTop: SCREEN_PADDING_SMALL,
  },
  creditPolicyHeader: {
    marginBottom: 16,
  },
  creditPolicyWrapper: {
    marginBottom: 16,
  },
  flex: {
    flex: 1,
  },
  ineligibleItem: {
    opacity: 0.4,
  },
  itemSeparator: {
    backgroundColor: theme.sk.color.divider.default,
    marginHorizontal: SCREEN_PADDING_SMALL,
  },
  loadingWrapper: {
    paddingVertical: SCREEN_PADDING,
  },
  noEligibleItemsContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: SCREEN_PADDING,
    paddingVertical: SCREEN_PADDING,
  },
  noEligibleItemsMessage: {
    alignItems: 'center',
  },
  noEligibleItemsTitle: {
    alignItems: 'center',
    marginBottom: 8,
  },
  searchWrapper: {
    padding: SCREEN_PADDING_SMALL,
  },
  sectionHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
    paddingHorizontal: SCREEN_PADDING_SMALL,
  },
}));
